import * as React from 'react';

import Button from '../../components/Button';
import Table from '../../components/Table';

import {
  PRIORITY_LABELS,
  PRIORITY_TONE,
  type IPriorityTone,
  type TransactionNode,
} from './transactionAnomalyFeedbackTypes';
import TransactionRow from './TransactionRow';

import type { TransactionAnomalyPriority as TransactionAnomalyPriorityEnum } from '../../__generated__/globalTypes';

export interface ICollapsibleSectionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  tone: IPriorityTone;
  children: React.ReactNode;
}

/**
 * Section header that toggles its body between visible and hidden. Uses a raw
 * `<button>` element rather than the mops-local `Button` component because
 * this affordance is a borderless, full-width header strip — the `Button`
 * primitive's chrome (uppercase, fixed padding, accent border) would actively
 * fight that layout. Keep raw markup until the affordance becomes a shared
 * pattern worth extracting.
 */
export const CollapsibleSection = ({ title, isOpen, onToggle, tone, children }: ICollapsibleSectionProps) => (
  <section className={`border-l-4 ${tone.border} ${tone.bg} flex flex-col`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className="flex w-full items-center justify-between px-4 py-3 text-left"
    >
      <span className={`text-sm font-semibold ${tone.text}`}>{title}</span>
      <span className={`transition-transform ${tone.text} ${isOpen ? 'rotate-180' : ''}`}>
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </span>
    </button>
    {isOpen ? <div className="flex flex-col gap-3 px-4 pb-4">{children}</div> : null}
  </section>
);

export interface IPrioritySectionProps {
  priority: TransactionAnomalyPriorityEnum;
  unreviewed: TransactionNode[];
  reviewed: TransactionNode[];
  isOpen: boolean;
  onToggle: () => void;
  merchantStatsById: Map<string, Record<string, unknown>>;
  detailedReasonMap: Map<string, string>;
  onDetailedReasonChange: (transactionId: string, value: string) => void;
  onToggleReviewed: (transaction: TransactionNode, checked: boolean) => Promise<void>;
  onSavePriority: (
    transactionId: string,
    priority: TransactionAnomalyPriorityEnum,
    detailedReason: string | null
  ) => Promise<void>;
  onMarkAll: (transactionIds: string[], priority: TransactionAnomalyPriorityEnum) => Promise<void>;
}

/**
 * Collapsible section for one priority bucket. Renders an unreviewed table
 * (with a "mark all reviewed" button) followed by a reviewed table. Shared
 * row-level UI lives in `TransactionRow`; this component only owns the
 * priority-level layout.
 */
const PrioritySection = ({
  priority,
  unreviewed,
  reviewed,
  isOpen,
  onToggle,
  merchantStatsById,
  detailedReasonMap,
  onDetailedReasonChange,
  onToggleReviewed,
  onSavePriority,
  onMarkAll,
}: IPrioritySectionProps) => {
  const tone = PRIORITY_TONE[priority];
  const title = `${PRIORITY_LABELS[priority]} · ${unreviewed.length} unreviewed · ${reviewed.length} reviewed`;
  return (
    <CollapsibleSection title={title} isOpen={isOpen} onToggle={onToggle} tone={tone}>
      {unreviewed.length > 0 ? (
        <div className="flex flex-col gap-2">
          <TransactionsTable
            transactions={unreviewed}
            label={`${priority} — unreviewed`}
            merchantStatsById={merchantStatsById}
            detailedReasonMap={detailedReasonMap}
            onDetailedReasonChange={onDetailedReasonChange}
            onToggleReviewed={onToggleReviewed}
            onSavePriority={onSavePriority}
          />
          <div className="flex justify-end">
            <Button
              size="xs"
              color="secondary"
              onClick={() => {
                void onMarkAll(
                  unreviewed.map((t) => t.id),
                  priority
                );
              }}
            >
              Mark all {priority} as reviewed
            </Button>
          </div>
        </div>
      ) : null}
      {reviewed.length > 0 ? (
        <TransactionsTable
          transactions={reviewed}
          label={`${priority} — reviewed`}
          merchantStatsById={merchantStatsById}
          detailedReasonMap={detailedReasonMap}
          onDetailedReasonChange={onDetailedReasonChange}
          onToggleReviewed={onToggleReviewed}
          onSavePriority={onSavePriority}
        />
      ) : null}
    </CollapsibleSection>
  );
};

interface ITransactionsTableProps {
  transactions: TransactionNode[];
  label: string;
  merchantStatsById: Map<string, Record<string, unknown>>;
  detailedReasonMap: Map<string, string>;
  onDetailedReasonChange: (transactionId: string, value: string) => void;
  onToggleReviewed: (transaction: TransactionNode, checked: boolean) => Promise<void>;
  onSavePriority: (
    transactionId: string,
    priority: TransactionAnomalyPriorityEnum,
    detailedReason: string | null
  ) => Promise<void>;
}

const TransactionsTable = ({
  transactions,
  label,
  merchantStatsById,
  detailedReasonMap,
  onDetailedReasonChange,
  onToggleReviewed,
  onSavePriority,
}: ITransactionsTableProps) => (
  <div className="flex flex-col gap-2">
    <h3 className="text-fg-tertiary text-xs uppercase tracking-wider">{label}</h3>
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell aria-label="Reviewed">
            <span aria-hidden="true">✓</span>
          </Table.HeaderCell>
          <Table.HeaderCell>
            <div className="w-12">Score</div>
          </Table.HeaderCell>
          <Table.HeaderCell>Anomaly reasons</Table.HeaderCell>
          <Table.HeaderCell>Date</Table.HeaderCell>
          <Table.HeaderCell>Transaction description</Table.HeaderCell>
          <Table.HeaderCell className="text-right">Amount</Table.HeaderCell>
          <Table.HeaderCell>Global stats</Table.HeaderCell>
          <Table.HeaderCell>Merchant global stats</Table.HeaderCell>
          <Table.HeaderCell>Merchant stats</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {transactions.map((transaction) => (
          <TransactionRow
            key={transaction.id}
            transaction={transaction}
            merchantStatsById={merchantStatsById}
            detailedReasonMap={detailedReasonMap}
            onDetailedReasonChange={onDetailedReasonChange}
            onToggleReviewed={onToggleReviewed}
            onSavePriority={onSavePriority}
          />
        ))}
      </Table.Body>
    </Table>
  </div>
);

export default PrioritySection;
